import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Separator } from "@/lib/shadcn/ui/separator";
import { cn } from "@/lib/shadcn/utils";

export interface DonutChartData {
    name: string;
    value: number;
    valueFormatter?: (val: number) => string;
    color?: string;
    [key: string]: any;
}

export interface DonutChartCardProps {
    data: DonutChartData[];
    title: string;
    subtitle: string;
    height?: number;
    className?: string;
}

// Default colors that match your screenshot
const COLORS = ["#E11D48", "#2563EB", "#F59E0B"];

const renderShape = (props: any) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value, isActive } = props;

    // If not active, render normal sector
    if (!isActive) {
        return <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />;
    }

    // Active sector rendering
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const labelX = cx + (outerRadius + 28) * cos;
    const labelY = cy + (outerRadius + 28) * sin;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
        <g>
            <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
            <Sector cx={cx} cy={cy} startAngle={startAngle} endAngle={endAngle} innerRadius={outerRadius + 6} outerRadius={outerRadius + 10} fill={fill} />
            <text x={labelX} y={labelY} textAnchor={textAnchor} className="text-sm font-medium" dy={-15}>
                {`${payload.name}: ${payload.valueFormatter ? payload.valueFormatter(value) : value}`}
            </text>
            <text x={labelX} y={labelY} dy={10} textAnchor={textAnchor} className="text-sm text-muted-foreground">
                {`(${(percent * 100).toFixed(2)}%)`}
            </text>
        </g>
    );
};

const DonutChartCard: React.FC<DonutChartCardProps> = ({ data, title, subtitle, height, className }) => {
    const chartHeight = height ?? 280;
    // When stacked at a reduced height (the two-pie layout), tighten the title so
    // the header doesn't dominate. When height is omitted (single-pie callers),
    // keep today's heavier title byte-for-byte so they're visually unchanged.
    const isCompact = height !== undefined && height < 280;
    const titleClass = isCompact ? "text-lg font-medium text-muted-foreground mb-2" : "text-2xl font-medium text-muted-foreground mb-6";

    return (
        <Card className={cn("shadow-sm flex flex-col", className)}>
            <CardHeader className="space-y-1">
                <CardTitle className={titleClass}>{title}</CardTitle>
                <div className="flex justify-between items-start">
                    <p className="text-lg font-semibold">{subtitle}</p>
                    <div className="space-y-3">
                        {data
                            .sort((a, b) => b.value - a.value)
                            .map((entry, index) => (
                                <div key={entry.name} className="flex items-center justify-end gap-2 text-sm">
                                    <div
                                        aria-hidden="true"
                                        className="h-3 w-3 rounded-full"
                                        style={{
                                            backgroundColor: entry.color || COLORS[index % COLORS.length],
                                        }}
                                    />
                                    <span>
                                        {entry.name}: {entry.valueFormatter ? entry.valueFormatter(entry.value) : entry.value}
                                    </span>
                                </div>
                            ))}
                    </div>
                </div>
            </CardHeader>
            <Separator />
            <CardContent className="flex-1 flex items-center justify-center py-6">
                <div data-testid="donut-chart-container" className="w-full" style={{ height: chartHeight }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie shape={renderShape} data={data} cx="50%" cy="50%" innerRadius="42%" outerRadius="60%" dataKey="value">
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={() => null} cursor={false} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default DonutChartCard;
